import React, { useEffect, useRef, useState } from "react";
import api, { fileUrl, formatApiError } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { toast } from "sonner";

export default function AdminSettings() {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const heroRef = useRef(null);

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {}); }, []);

  if (!s) return <div className="overline text-neutral-400">Loading...</div>;

  const update = (k, v) => setS({ ...s, [k]: v });
  const updateNum = (k, v) => setS({ ...s, [k]: v === "" ? "" : parseFloat(v) });

  const onHeroFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingHero(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS((prev) => ({ ...prev, hero_workshop_image: data.path }));
      toast.success("Foto subida. No olvides Guardar para aplicarla.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingHero(false); if (heroRef.current) heroRef.current.value = ""; }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        print_rate_per_m2: parseFloat(s.print_rate_per_m2) || 0,
        print_min: parseFloat(s.print_min) || 0,
        gallery_pct: parseFloat(s.gallery_pct) || 0,
        referrer_pct: parseFloat(s.referrer_pct) || 0,
        referral_cookie_days: parseInt(s.referral_cookie_days) || 30,
        exclusivity_premium_pct: parseFloat(s.exclusivity_premium_pct) || 0.5,
        default_irpf_pct: parseFloat(s.default_irpf_pct) || 0.15,
        hero_workshop_image: s.hero_workshop_image || null,
        max_uploads_per_week: parseInt(s.max_uploads_per_week) || 10,
        max_pending_per_artist: parseInt(s.max_pending_per_artist) || 15,
      });
      toast.success(t.adminSettings.saved);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full border border-neutral-300 px-4 py-3 outline-none focus:border-black bg-white text-sm";

  // Live example preview
  const ex_area = 0.8; // 100x80
  const ex_cost = Math.max(s.print_min || 0, (s.print_rate_per_m2 || 0) * ex_area);
  const ex_net = 180;
  const margin = 1 - (s.gallery_pct || 0) - (s.referrer_pct || 0);
  const ex_retail = margin > 0 ? (ex_net + ex_cost) / margin : 0;

  return (
    <div data-testid="admin-settings">
      <h1 className="font-serif text-4xl font-light tracking-tight mb-2">{t.adminSettings.title}</h1>
      <p className="text-sm text-neutral-500 mb-8">{t.adminSettings.subtitle}</p>

      <form onSubmit={save} className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-5">
          <div>
            <label className="overline text-neutral-500 mb-2 block">{t.adminSettings.printRate}</label>
            <input type="number" step="0.01" value={s.print_rate_per_m2} onChange={(e) => updateNum("print_rate_per_m2", e.target.value)} className={inputCls} data-testid="set-print-rate" />
            <p className="text-xs text-neutral-400 mt-1">{t.adminSettings.printRateHint}</p>
          </div>
          <div>
            <label className="overline text-neutral-500 mb-2 block">{t.adminSettings.printMin}</label>
            <input type="number" step="0.01" value={s.print_min} onChange={(e) => updateNum("print_min", e.target.value)} className={inputCls} data-testid="set-print-min" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline text-neutral-500 mb-2 block">{t.adminSettings.galleryPct}</label>
              <input type="number" step="0.01" min="0" max="1" value={s.gallery_pct} onChange={(e) => updateNum("gallery_pct", e.target.value)} className={inputCls} data-testid="set-gallery-pct" />
              <p className="text-xs text-neutral-400 mt-1">0.20 = 20%</p>
            </div>
            <div>
              <label className="overline text-neutral-500 mb-2 block">{t.adminSettings.referrerPct}</label>
              <input type="number" step="0.01" min="0" max="1" value={s.referrer_pct} onChange={(e) => updateNum("referrer_pct", e.target.value)} className={inputCls} data-testid="set-referrer-pct" />
              <p className="text-xs text-neutral-400 mt-1">0.10 = 10%</p>
            </div>
          </div>
          <div>
            <label className="overline text-neutral-500 mb-2 block">{t.adminSettings.cookieDays}</label>
            <input type="number" value={s.referral_cookie_days} onChange={(e) => updateNum("referral_cookie_days", e.target.value)} className={inputCls} data-testid="set-cookie-days" />
          </div>
          <div>
            <label className="overline text-neutral-500 mb-2 block">Premium de exclusividad</label>
            <input type="number" step="0.05" min="0" max="5" value={s.exclusivity_premium_pct ?? 0.5} onChange={(e) => updateNum("exclusivity_premium_pct", e.target.value)} className={inputCls} data-testid="set-exclusivity-pct" />
            <p className="text-xs text-neutral-400 mt-1">
              Multiplicador extra sobre el valor de las impresiones restantes para la compra en exclusiva. 0.5 = +50% sobre el valor base.
            </p>
          </div>
          <div>
            <label className="overline text-neutral-500 mb-2 block">IRPF por defecto a artistas</label>
            <input type="number" step="0.01" min="0" max="0.5" value={s.default_irpf_pct ?? 0.15} onChange={(e) => updateNum("default_irpf_pct", e.target.value)} className={inputCls} data-testid="set-irpf-pct" />
            <p className="text-xs text-neutral-400 mt-1">
              Retención aplicada por defecto al liquidar (15% estándar autónomos). El artista puede tener su % propio en su perfil.
            </p>
          </div>
          <div className="pt-4 border-t border-neutral-200">
            <label className="overline text-neutral-500 mb-2 block">Límites anti-flood (subidas)</label>
            <p className="text-xs text-neutral-500 mb-3">Evita avalanchas de subidas que sobrecarguen la curación y el envío de emails.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-neutral-600 mb-1 block">Máx. obras / 7 días por artista</label>
                <input type="number" min="1" max="100" value={s.max_uploads_per_week ?? 10} onChange={(e) => updateNum("max_uploads_per_week", e.target.value)} className={inputCls} data-testid="set-max-uploads-week" />
              </div>
              <div>
                <label className="text-xs text-neutral-600 mb-1 block">Máx. obras pendientes simultáneas</label>
                <input type="number" min="1" max="200" value={s.max_pending_per_artist ?? 15} onChange={(e) => updateNum("max_pending_per_artist", e.target.value)} className={inputCls} data-testid="set-max-pending" />
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-neutral-200">
            <label className="overline text-neutral-500 mb-2 block">Imagen &quot;Vender&quot; (página pública)</label>
            <p className="text-xs text-neutral-500 mb-3">Sube una foto de la fachada del taller, máquinas o ambiente. Aparece en el hero de la página <strong>Vender</strong>. Recomendado: vertical 4:5, 1200px+ en el lado mayor.</p>
            <div className="flex items-start gap-4">
              <div className="w-32 aspect-[4/5] bg-neutral-100 overflow-hidden border border-neutral-200 shrink-0">
                {s.hero_workshop_image ? (
                  <img src={fileUrl(s.hero_workshop_image)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center overline text-[10px] text-neutral-400">Sin imagen</div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label className="cursor-pointer inline-block">
                  <input type="file" accept="image/*" ref={heroRef} onChange={onHeroFile} className="hidden" disabled={uploadingHero} data-testid="hero-file-input" />
                  <span className="inline-block overline text-xs px-4 py-2 border border-neutral-300 hover:bg-neutral-50">
                    {uploadingHero ? "Subiendo..." : (s.hero_workshop_image ? "Reemplazar" : "Subir foto")}
                  </span>
                </label>
                {s.hero_workshop_image && (
                  <button type="button" onClick={() => setS((p) => ({ ...p, hero_workshop_image: null }))} className="overline text-xs text-red-600 hover:underline text-left" data-testid="hero-remove">
                    Quitar imagen
                  </button>
                )}
              </div>
            </div>
          </div>
          <button disabled={saving} type="submit" className="bg-black text-white px-10 py-4 overline hover:bg-neutral-800 transition-colors disabled:opacity-50" data-testid="settings-save">
            {saving ? "..." : t.adminSettings.save}
          </button>
        </div>

        <div className="bg-white border border-neutral-200 p-6">
          <div className="overline text-neutral-500 mb-4">{t.adminSettings.preview}</div>
          <p className="text-sm text-neutral-600 mb-4">{t.adminSettings.previewHint}</p>
          <div className="space-y-2 text-sm border-t border-neutral-200 pt-4">
            <div className="flex justify-between"><span>{t.adminSettings.exNet}</span><span>€{ex_net.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>{t.adminSettings.exCost} (80×100cm)</span><span>€{ex_cost.toFixed(2)}</span></div>
            <div className="flex justify-between text-neutral-500"><span>{t.adminSettings.exGallery}</span><span>{((s.gallery_pct || 0) * 100).toFixed(0)}%</span></div>
            <div className="flex justify-between text-neutral-500"><span>{t.adminSettings.exReferrer}</span><span>{((s.referrer_pct || 0) * 100).toFixed(0)}%</span></div>
            <div className="border-t border-neutral-200 pt-3 mt-3 flex justify-between font-serif text-2xl">
              <span>{t.adminSettings.exRetail}</span><span data-testid="preview-retail">€{ex_retail.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
